// src/components/GoalEditSheet.js
//
// One race, as a sheet: the form the Goal & plan panel used to unfold inline
// (text fields, a native date input, chips) is now the same instrument the
// Adjust-goal sheet is — rows that each open a wheel or a segmented choice,
// the value always readable in the row. Used for a new race, a B/C race on
// the way, an ambition without a date, and the A goal when edited from
// Settings.

import React, { useEffect, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { useSheetPull } from "./useSheetPull";
import { PickerWheel } from "./PickerWheel";
import { api } from "../api";

export const PRESETS = [
  { key: "5K", label: "5K", km: 5 },
  { key: "10K", label: "10K", km: 10 },
  { key: "HM", label: "Half", km: 21.0975 },
  { key: "M", label: "Marathon", km: 42.195 },
  { key: "50K", label: "50K", km: 50 },
  { key: "100K", label: "100K", km: 100 },
  { key: "100M", label: "100M", km: 161 },
  { key: "custom", label: "Custom", km: 0 },
];
export const TERRAINS = [["road", "Road"], ["trail", "Trail"], ["mixed", "Mixed"], ["mountain", "Mountain"]];
export const terrainLabel = (t) => { const f = TERRAINS.find((x) => x[0] === t); return f ? f[1] : "Road"; };
export const PRIORITY_TEXT = {
  A: "Main goal. It drives the whole plan.",
  B: "Race it. A mini-taper before, recovery after.",
  C: "Train through. A hard long run with a bib.",
};
const PRIORITY_SHORT = { A: "Main goal", B: "Race it", C: "Train through" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n) => String(n).padStart(2, "0");
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const localIso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export function fmtHms(sec) {
  if (sec == null) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.round(sec % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}
/* The time as the wheel can hold it: h:mm, seconds only for road distances. */
export function timeLabel(sec, withSec) {
  if (sec == null) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return withSec ? `${h}:${pad2(m)}:${pad2(s)}` : `${h}:${pad2(m)}`;
}
export function presetFor(km) {
  const p = PRESETS.find((x) => x.km > 0 && Math.abs(x.km - km) < (x.km >= 100 ? 2 : 0.6));
  return p ? p.key : "custom";
}
export function distanceLabel(km) {
  const p = presetFor(km);
  if (p === "HM") return "Half marathon";
  if (p === "M") return "Marathon";
  if (p !== "custom") return p;
  return km ? `${Math.round(km)}K` : "";
}
export function fmtDateDow(iso) {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—";
}
export function fmtDateShort(iso) {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "—";
}
export function weeksUntil(iso) {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? Math.max(0, Math.round((+d - Date.now()) / (7 * 864e5))) : null;
}
/* The race colours the app already uses for distances. */
export function raceVar(km) {
  if (Math.abs(km - 10) < 0.2) return "var(--race-10k)";
  if (Math.abs(km - 21.0975) < 0.5) return "var(--race-half)";
  if (Math.abs(km - 42.195) < 0.8) return "var(--race-marathon)";
  return "var(--race-ultra)";
}
const defaultTargetSec = (km) => (km >= 45 ? 12 * 3600 : km > 21.2 ? 3 * 3600 + 30 * 60 : 45 * 60);

export default function GoalEditSheet({ goal, hasA, onClose, onSaved, onRemoved }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dirtyRef = useRef(false);
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef });

  const isNew = !goal;
  const g0 = goal || {};
  const [preset, setPreset] = useState(goal ? presetFor(Number(g0.distanceKm) || 0) : hasA ? "M" : "100K");
  const [customKm, setCustomKm] = useState(goal && presetFor(Number(g0.distanceKm) || 0) === "custom" ? String(Math.round(Number(g0.distanceKm))) : "");
  const [name, setName] = useState(g0.name || "");
  const [someday, setSomeday] = useState(goal ? !g0.raceDate : false);
  const [raceDate, setRaceDate] = useState(g0.raceDate || "");
  const [targetSec, setTargetSec] = useState(g0.targetFinishSec != null && g0.targetFinishSec > 0 ? Math.floor(g0.targetFinishSec) : null);
  const [priority, setPriority] = useState(g0.priority || (hasA ? "B" : "A"));
  const [terrain, setTerrain] = useState(g0.terrain || "road");
  const [elev, setElev] = useState(Math.max(0, Math.round(Number(g0.elevationGain) || 0)));
  const [openRow, setOpenRow] = useState(isNew ? "distance" : null);
  const [seed, setSeed] = useState(0);
  const [lastSec, setLastSec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const rowRefs = useRef({});

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !dirtyRef.current) closeRef.current && closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const km = preset === "custom" ? Number(customKm) || 0 : PRESETS.find((p) => p.key === preset).km;
  const withSec = km > 0 && km <= 42.195;
  const normSec = (s) => (s == null ? null : withSec ? s : s - (s % 60));
  const todayIso = localIso(new Date());
  const draft = { km, name: name.trim(), someday, raceDate: someday ? "" : raceDate, targetSec: normSec(targetSec), priority, terrain, elev };
  const initial = {
    km: Number(g0.distanceKm) || 0,
    name: String(g0.name || "").trim(),
    someday: goal ? !g0.raceDate : false,
    raceDate: g0.raceDate || "",
    targetSec: g0.targetFinishSec != null && g0.targetFinishSec > 0 ? normSec(Math.floor(g0.targetFinishSec)) : null,
    priority: g0.priority || (hasA ? "B" : "A"),
    terrain: g0.terrain || "road",
    elev: Math.max(0, Math.round(Number(g0.elevationGain) || 0)),
  };
  const dirty = isNew || JSON.stringify(draft) !== JSON.stringify(initial);
  dirtyRef.current = !isNew && dirty;

  const liveError = !km ? "Set the distance." : !someday && raceDate && raceDate < todayIso ? "The race date is in the past." : targetSec === 0 ? "Pick a time above 0:00." : null;
  const typeCol = raceVar(km || 100);
  const datePast = !!g0.raceDate && g0.raceDate < todayIso;

  // Wheel positions.
  const wheelSec = targetSec != null ? targetSec : (lastSec || defaultTargetSec(km));
  const hh = Math.floor(wheelSec / 3600);
  const mm = Math.floor((wheelSec % 3600) / 60);
  const ss = Math.floor(wheelSec % 60);
  const dParts = (raceDate || todayIso).split("-").map(Number);
  const dy = dParts[0] || new Date().getFullYear();
  const dm = dParts[1] || 1;
  const dd = dParts[2] || 1;
  const thisYear = new Date().getFullYear();
  const [yearBounds] = useState(() => { const sy = Number((g0.raceDate || "").slice(0, 4)) || thisYear; return [Math.min(thisYear, sy), Math.max(thisYear + 3, sy)]; });
  const y0 = Math.min(yearBounds[0], dy);
  const y1 = Math.max(yearBounds[1], dy);
  const daysMax = daysInMonth(dy, dm);

  const setTime = (h, m, s) => setTargetSec(h * 3600 + m * 60 + (withSec ? s : 0));
  const setDate = (y, m, d) => setRaceDate(`${y}-${pad2(m)}-${pad2(Math.min(d, daysInMonth(y, m)))}`);
  const toggleRow = (row) => {
    const next = openRow === row ? null : row;
    setOpenRow(next);
    // A date row with nothing in it starts twelve weeks out, so the wheels
    // show a real date and the row reads one.
    if (next === "date" && !someday && !raceDate) {
      const d = new Date(); d.setDate(d.getDate() + 84); setRaceDate(localIso(d));
    }
    if (next) setTimeout(() => { try { const el = rowRefs.current[next]; el && el.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch (_) {} }, 280);
  };
  const requestClose = () => { if (!dirtyRef.current) onClose(); };
  const clearTime = () => { setLastSec(targetSec); setTargetSec(null); };
  const restoreTime = () => { setTargetSec(lastSec || defaultTargetSec(km)); setSeed((s) => s + 1); };
  const toggleSomeday = () => {
    if (someday) { setSomeday(false); if (!raceDate) { const d = new Date(); d.setDate(d.getDate() + 84); setRaceDate(localIso(d)); } }
    else setSomeday(true);
  };

  const save = async () => {
    if (liveError) { setError(liveError); return; }
    if (!someday && !raceDate) { setError("Pick a race date, or make it an ambition without one."); return; }
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/api/goals", {
        id: goal ? goal.id : undefined,
        name: name.trim() || undefined,
        distanceKm: km,
        raceDate: someday ? undefined : raceDate,
        targetFinishSec: targetSec != null && targetSec > 0 ? normSec(targetSec) : undefined,
        priority,
        terrain,
        elevationGain: Number(elev) || 0,
        status: someday ? "someday" : goal && goal.status !== "someday" ? goal.status : "planned",
      });
      onSaved(r.goals);
    } catch (err) {
      setError(err && err.message ? err.message : "Could not save the goal.");
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!goal) return;
    if (!window.confirm(`Remove ${goal.name || distanceLabel(km)}?`)) return;
    setBusy(true);
    try {
      const r = await api.del(`/api/goals/${goal.id}`);
      onRemoved(r.goals);
    } catch (err) {
      setError(err && err.message ? err.message : "Could not remove the goal.");
      setBusy(false);
    }
  };
  const markDone = async () => {
    if (!goal) return;
    setBusy(true);
    try {
      const r = await api.post("/api/goals", Object.assign({}, goal, { status: "done" }));
      onSaved(r.goals);
    } catch (err) {
      setError(err && err.message ? err.message : "Could not update the goal.");
      setBusy(false);
    }
  };

  const title = isNew ? "New race" : (g0.name || distanceLabel(initial.km) || "Race");
  const when = isNew ? "Add a race" : someday ? "Ambition" : priority === "A" ? "Goal race" : "On the way";
  const errText = error || null;
  const rowCls = (key, invalid) => "agRow geRow geRow--" + key + (openRow === key ? " is-open" : "") + (invalid ? " is-invalid" : "");
  const row = (key, label, value, invalid, children) => (
    <div ref={(el) => { rowRefs.current[key] = el; }} className={rowCls(key, invalid)}>
      <button type="button" className="agRow__btn" aria-expanded={openRow === key} aria-controls={`gePick-${key}`} onClick={() => toggleRow(key)}>
        <span className="tsAdjust__label">{label}</span>
        <span className="agRow__value">{value}<ChevronDown size={16} className="agRow__chev" aria-hidden="true" /></span>
      </button>
      <div className="agPick" id={`gePick-${key}`} aria-hidden={openRow !== key}>
        <div className="agPick__in">{openRow === key ? children : null}</div>
      </div>
    </div>
  );
  const seg = (opts, cur, set, cls) => (
    <div className={"geSeg" + (cls ? " " + cls : "")} role="radiogroup">
      {opts.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={cur === v} className={"geSeg__opt" + (cur === v ? " is-on" : "")} onClick={() => set(v)}>{l}</button>
      ))}
    </div>
  );

  const wksLabel = !someday && raceDate ? weeksUntil(raceDate) : null;
  const primaryLabel = busy ? "Saving…" : "Save";

  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div ref={sheetRef} className="bottomSheetPanel tsSheet agSheet geSheet" style={{ "--type": typeCol }} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label={isNew ? "New race" : "Edit race"}>
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">{title}</div>
            <div className="tsHead__date">
              <span className="tsHead__when">{when}</span>
              {km ? <><span className="tsHead__sep" />{distanceLabel(km)}</> : null}
              {wksLabel != null ? <><span className="tsHead__sep" />{wksLabel} wks</> : null}
            </div>
          </div>
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>

        <div ref={bodyRef} className="bottomSheetBody tsBody agBody geBody">
          <div className="tsAdjust agRows">
            {row("distance", "Distance", km ? `${distanceLabel(km)}${preset === "custom" || presetFor(km) === "custom" ? "" : ` · ${Number.isInteger(km) ? km : km.toFixed(1)} km`}` : "Set distance", !km, (
              <>
                <div className="geChips" role="radiogroup" aria-label="Distance">
                  {PRESETS.map((p) => (
                    <button key={p.key} type="button" role="radio" aria-checked={preset === p.key} className={"geChip" + (preset === p.key ? " is-on" : "")} onClick={() => { setPreset(p.key); setSeed((s) => s + 1); }}>{p.label}</button>
                  ))}
                </div>
                {preset === "custom" ? (
                  <div className="geField">
                    <input className="geInput" type="number" inputMode="numeric" min={1} max={400} placeholder="Distance in km" aria-label="Distance in km" value={customKm} onChange={(e) => setCustomKm(e.target.value)} />
                    <span className="geField__unit">km</span>
                  </div>
                ) : null}
              </>
            ))}
            {row("name", "Name", name.trim() ? name.trim() : <span className="geRow__muted">Optional</span>, false, (
              <div className="geField">
                <input className="geInput" type="text" placeholder={distanceLabel(km || 10)} aria-label="Race name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus />
              </div>
            ))}
            {row("date", "Race date", someday ? "No date yet" : raceDate ? fmtDateDow(raceDate) : <span className="geRow__muted">Pick a date</span>, !someday && !!raceDate && raceDate < todayIso, (
              <>
                {!someday ? (
                  <>
                    <div className="gwWheels agWheels" key={`dw-${seed}`}>
                      <div className="agWheels__band" aria-hidden="true" />
                      <PickerWheel min={1} max={daysMax} initial={dd} ariaLabel="Day" onChange={(v) => setDate(dy, dm, v)} />
                      <PickerWheel min={1} max={12} initial={dm} labels={MONTHS} ariaLabel="Month" onChange={(v) => setDate(dy, v, dd)} />
                      <PickerWheel min={y0} max={y1} initial={dy} ariaLabel="Year" onChange={(v) => setDate(v, dm, dd)} />
                    </div>
                    <div className="gwWunit agWunit"><span>day</span><span>month</span><span>year</span></div>
                  </>
                ) : (
                  <div className="geHint">An ambition sits beside the plan without a date. The coach builds toward it; give it a date when you have one.</div>
                )}
                <button type="button" className="agLink" onClick={toggleSomeday}>{someday ? "Set a date" : "No date yet, an ambition"}</button>
              </>
            ))}
            {row("time", "Target time", targetSec == null ? "Finish" : `${timeLabel(normSec(targetSec), withSec)} h`, targetSec === 0, (
              <>
                <div className={"gwWheels agWheels" + (targetSec == null ? " is-off" : "")} key={`tw-${seed}`} aria-disabled={targetSec == null}>
                  <div className="agWheels__band" aria-hidden="true" />
                  <PickerWheel min={0} max={99} initial={hh} ariaLabel="Hours" onChange={(v) => setTime(v, mm, ss)} />
                  <div className="gwWsep">:</div>
                  <PickerWheel min={0} max={59} initial={mm} ariaLabel="Minutes" onChange={(v) => setTime(hh, v, ss)} />
                  {withSec ? <><div className="gwWsep">:</div><PickerWheel min={0} max={59} initial={ss} ariaLabel="Seconds" onChange={(v) => setTime(hh, mm, v)} /></> : null}
                </div>
                <div className="gwWunit agWunit"><span>h</span><span className="sep" /><span>min</span>{withSec ? <><span className="sep" /><span>sec</span></> : null}</div>
                <button type="button" className="agLink" onClick={targetSec == null ? restoreTime : clearTime}>{targetSec == null ? "Set a time" : "No target time"}</button>
              </>
            ))}
            {!someday ? row("priority", "Priority", `${priority} · ${PRIORITY_SHORT[priority]}`, false, (
              <>
                {seg([["A", "A"], ["B", "B"], ["C", "C"]], priority, setPriority, "geSeg--3")}
                <div className="geHint">{PRIORITY_TEXT[priority]}{priority === "A" && hasA && !(goal && goal.priority === "A") ? " Replaces the current main goal." : ""}</div>
              </>
            )) : null}
            {row("terrain", "Surface", terrainLabel(terrain), false, (
              <>
                {seg(TERRAINS, terrain, setTerrain, "geSeg--4")}
                <div className="geHint">{terrain === "road" ? "Flat and paced. No elevation targets, no hill sessions." : terrain === "trail" ? "Elevation targets and hill work in the build. Paces read slower." : terrain === "mixed" ? "Some climbing. Lighter elevation targets than trail." : "Big vertical. Hiking is part of the specific phase."}</div>
                {terrain !== "road" ? (
                  <div className="tsAdjust__row geElev">
                    <span className="tsAdjust__label">Elevation gain</span>
                    <div className="tsStepper">
                      <button type="button" className="tsStepper__btn" aria-label="Less elevation" onClick={() => setElev((v) => Math.max(0, v - 100))}>−</button>
                      <span className="tsStepper__v">{elev.toLocaleString("en-GB")}<span className="tsStepper__u">m</span></span>
                      <button type="button" className="tsStepper__btn" aria-label="More elevation" onClick={() => setElev((v) => Math.min(20000, v + 100))}>+</button>
                    </div>
                  </div>
                ) : null}
              </>
            ))}
          </div>

          {!isNew ? (
            <div className="geLinks">
              {datePast && goal.status !== "done" ? <button type="button" className="agLink" onClick={markDone} disabled={busy}>Mark as done</button> : null}
              <button type="button" className="agLink geLink--danger" onClick={remove} disabled={busy}>Remove this race</button>
            </div>
          ) : null}
          {errText ? <div className="agError" role="alert">{errText}</div> : null}
        </div>

        <div className="tsActions">
          <button type="button" className="sessionSheetBtn sessionSheetBtn--secondary" onClick={onClose}>{dirty && !isNew ? "Cancel" : "Close"}</button>
          <button type="button" className="sessionSheetBtn sessionSheetBtn--primary" disabled={!dirty || busy || !!liveError} aria-disabled={!dirty || busy || !!liveError} onClick={save}>
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
